import React, { useContext, useEffect, useState } from 'react';
import {
  Avatar,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@material-ui/core';
import { Notifications, Today } from '@material-ui/icons';

import moment from 'moment';

import PropTypes from 'prop-types';

import SearchField from './searchField';
import TablePaginationActions from './tablePaginationActions';

import { filteredSearch, getColors, getComparator, remainingDays, stableSort } from '../../utils/helpers';
import { defaultRowsPerPage, defaultSortOrder, extraDays, rowsPerPageOptions, greenColor, amberColor, redColor, orangeColor } from '../../utils/constants';

import translations from '../../languages/i18n';

import { customOptionsTableStyles } from '../../styles/formElementsStyles';

import { SessionContext } from '../../utils/context';

export default function SchedulesList({ editScheduleData, schedulesData, userType, userInfo }) {
  const classesForm = customOptionsTableStyles();

  const sessionContext = useContext(SessionContext);

  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [formattedscheduleData, setFormattedscheduleData] = useState([]);
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('from'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchString, setSearchString] = useState('');

  const getAdaptedFormat = (dataList, email) => {
    const out = [];
    dataList.map((item) => {
      const N = item.daysToFillIn + extraDays;
      const valueState = remainingDays(item.from, item.daysToFillIn);
      out.push({
        _id: item._id,
        from: item.from,
        scheduleName: item.scheduleName,
        translatedScheduleName: translations[item.scheduleName],
        email: userType === 'clinician' ? email : '',
        programName: item.programName,
        to: moment(item.from).add(item.daysToFillIn, 'days'),
        state: valueState,
        // item.patientUserName arriba buit del servidor si el clínic no està a l'equip de la subsripció
        userName: userType === 'clinician' ? item.patientUserName : '',
        editable: item.editableByClinician,
        colors: getColors(N),
        started: item.started || null
      });
    });
    return out;
  };

  useEffect(() => {
    const schedulesDataAdapted = getAdaptedFormat(schedulesData, userInfo.email);
    setFormattedscheduleData(schedulesDataAdapted);
  }, [schedulesData]);

  useEffect(() => {
    if (formattedscheduleData) {
      setFilteredSchedules(formattedscheduleData.slice());
      setSearchString('');
    }
  }, [formattedscheduleData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const searchSchedule = (string) => {
    setPage(0);
    setSearchString(string);
    if (userType === 'clinician') {
      setFilteredSchedules(filteredSearch(formattedscheduleData, string, ['translatedScheduleName', 'programName', 'userName']));
    } else {
      setFilteredSchedules(filteredSearch(formattedscheduleData, string, ['translatedScheduleName', 'programName']));      
    }
  };

  const sendNewSchedule = (dateTo, idSchedule) => {
    const schedule = schedulesData.filter((item) => item._id === idSchedule);
    const newDaysToFillIn = moment(dateTo).diff(moment(schedule[0].from), 'd');
    // newDaysToFillIn no inclou el día de la nova data TO.
    // Incrementam 1 per a que s'inclogui
    const out = { ...schedule[0], daysToFillIn: newDaysToFillIn + 1 };
    editScheduleData(out, idSchedule, sessionContext.userToken);
  };

  const setMinDate = (from) => {
    const today = moment().format('YYYY-MM-DD');
    const firstDay = moment(from).format('YYYY-MM-DD');
    if (moment(today).isSameOrAfter(firstDay)) return today;
    return firstDay;
  };

  const schedulesDataColumns = 7; // Per saber el nombre de propietats de cada objecte
  return (
    <div>
      <TableContainer component={Paper}>
        <Table aria-label='schedule list table'>
          <TableHead>
            <TableRow>
              {/* ATENCIÓ: si pintam la columna _id afegir +1 al colSpan */}
              <TableCell colSpan={schedulesDataColumns + 2} align='left' padding='none'>
                <SearchField handleChange={(string) => searchSchedule(string)} value={searchString} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow className={classesForm.tableRowHeaderBgColor}>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'>
                <TableSortLabel
                  active={orderBy === 'scheduleName'}
                  direction={orderBy === 'scheduleName' ? order : 'asc'}
                  onClick={() => handleRequestSort('scheduleName')}
                >
                  <strong>{translations['_scheduleName']}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align='left'><strong>{translations['_state']}</strong></TableCell>
              <TableCell align='left'>
                <TableSortLabel
                  active={orderBy === 'from'}
                  direction={orderBy === 'from' ? order : 'asc'}
                  onClick={() => handleRequestSort('from')}
                >
                  <strong>{translations['_from']}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align='left'>
                <TableSortLabel
                  active={orderBy === 'to'}
                  direction={orderBy === 'to' ? order : 'asc'}
                  onClick={() => handleRequestSort('to')}
                >
                  <strong>{translations['_to']}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align={userType === 'clinician' ? 'left' : 'center'}><strong>{userType === 'clinician' ? translations['_patient'] : translations['_notify']}</strong></TableCell>
              {userType === 'clinician' && <TableCell align='left'><strong>{translations['_contact']}</strong></TableCell>}
              <TableCell align='left'><strong>{translations['_program']}</strong></TableCell>
            </TableRow>
            {(rowsPerPage > 0
              // ATENCIÓ: l'ordenació es fa damunt filteredSchedules, si no s'ha introduit res al camp de cerca el filteredSchedules inclou tot el schedulesData,
              // és a dir, l'ordenació no es fa sobre la "pagina de formularis" que es mostra al llistat (estigui filtrada o no)
              ? stableSort(filteredSchedules, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : stableSort(filteredSchedules, getComparator(order, orderBy))
            ).map((data, index) => (
              <TableRow key={data._id} className={index % 2 ? classesForm.tableRowBgColor1 : classesForm.tableRowBgColor2} hover>
                {/* ATENCIÓ: no pintam la columna _id */}
                <TableCell align='left'>{translations[data.scheduleName]}</TableCell>
                <TableCell align='center'>
                  {data.state === 0
                    ? <Avatar variant='rounded' style={{ color: '#fff', backgroundColor: greenColor, height: 30 }}><Today /></Avatar>
                    : data.state < 0
                      ? <Avatar variant='rounded' style={{ color: '#fff', backgroundColor: redColor, height: 30 }}>{''}</Avatar>
                      : data.state <= data.colors.red 
                        ? <Avatar variant='rounded' style={{ color: '#fff', backgroundColor: redColor, height: 30 }}>{data.state}</Avatar>
                        : data.state <= data.colors.amber 
                          ? <Avatar variant='rounded' style={{ color: '#fff', backgroundColor: data.started ? amberColor : orangeColor, height: 30 }}>{data.state}</Avatar>
                          : <Avatar variant='rounded' style={{ color: '#fff', backgroundColor: greenColor, height: 30 }}>{data.state}</Avatar>}
                </TableCell>
                <TableCell align='left'>{data.from && moment(data.from).format('DD-MM-YYYY')}</TableCell>
                <TableCell align='left'>
                  {/* En cas de que el clínic pugui modificar la data de fi del formulari, només ho farà si el formulari està obert (si data.state no és null) */}
                  {(data.editable && (
                    <TextField
                      disabled={data.state === null}
                      type='date'
                      value={moment(data.to).format('YYYY-MM-DD')}
                      onChange={(event) => {
                        sendNewSchedule(moment(event.target.value).format('YYYY-MM-DD'), data._id);
                      }}
                      // inputProps={{ min: moment(data.to).format('YYYY-MM-DD') }}
                      inputProps={{ min: setMinDate(data.from) }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )) || (
                    data.to && moment(data.to).format('DD-MM-YYYY')
                  )}
                </TableCell>
                {/* ATENCIÓ: Només un Admin pot ampliar el plaç de resposta d'un formulari */}
                <TableCell align={userType === 'clinician' ? 'left' : 'center'}>
                  {(userType === 'clinician' && (
                    data.userName || ' ' // Si data.userName és '' mostrarm un espai en blanc. Si no faig això quan data.userName és '' la condició es considera falsa i es pinta la campaneta de notificacions
                  )) || (
                  <IconButton disabled onClick={() => console.log('on click NOTIFICAR')}>
                    <Notifications />
                  </IconButton>
                  )}
                </TableCell>
                {userType === 'clinician' && <TableCell align='left'>{data.email}</TableCell>}
                <TableCell align='left'>{data.programName}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={schedulesDataColumns + 2}
                count={filteredSchedules.length}
                rowsPerPageOptions={rowsPerPageOptions}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                labelDisplayedRows={
                  ({ from, to, count }) => (
                    from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)
                  )
                }
                labelRowsPerPage={translations['_rowsPerPage']}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </div>
  );
}

// Indica i controla els props que se han de passar al component
SchedulesList.propTypes = {
  editScheduleData: PropTypes.func.isRequired,
  schedulesData: PropTypes.array.isRequired,
  userType: PropTypes.string.isRequired,
  userInfo: PropTypes.object.isRequired,
};
